import { Link, NavLink, useNavigate } from "react-router-dom";
import { Moon, Sun, Menu, X, User as UserIcon, LogIn } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/hooks/useTheme";
import { useAuth } from "@/hooks/useAuth";
import { TESTIDS } from "@/constants/testIds";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/play", label: "Play", testId: TESTIDS.navPlay },
  { to: "/lobby", label: "Lobby", testId: "nav-lobby" },
  { to: "/leaderboard", label: "Leaderboard", testId: "nav-leaderboard" },
  { to: "/learn", label: "Learn", testId: TESTIDS.navLearn },
  { to: "/rules", label: "Rules", testId: TESTIDS.navRules },
  { to: "/variants", label: "Variants", testId: TESTIDS.navVariants },
  { to: "/statistics", label: "Stats", testId: TESTIDS.navStats },
];

export function SiteHeader() {
  const { theme, toggle } = useTheme();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid={TESTIDS.siteHeader}
      className="sticky top-0 z-50 border-b border-border/60 glass-card"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" data-testid={TESTIDS.navHome} className="flex items-center gap-2 group">
          <Wordmark className="h-8 w-auto text-foreground" />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) =>
                cn(
                  "px-3 py-2 rounded-full text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-foreground/80 hover:text-foreground hover:bg-muted/60"
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1.5">
          <Button
            data-testid={TESTIDS.themeToggle}
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label="Toggle color theme"
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          {!loading && user ? (
            <>
              {(user.role === "admin" || user.role === "super_admin") && (
                <Button
                  data-testid="nav-admin"
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/admin")}
                  className="rounded-full px-3 hidden sm:inline-flex text-primary"
                >
                  {user.role === "super_admin" ? "Super admin" : "Admin"}
                </Button>
              )}
              <Button
                data-testid="nav-profile"
                variant="ghost"
                size="sm"
                onClick={() => navigate("/profile")}
                className="rounded-full px-3 hidden sm:inline-flex"
              >
                <UserIcon className="h-4 w-4 mr-1.5" />
                <span className="font-medium">{user.username}</span>
              </Button>
            </>
          ) : !loading ? (
            <>
              <Button
                data-testid="nav-login"
                variant="ghost"
                size="sm"
                onClick={() => navigate("/login")}
                className="rounded-full hidden sm:inline-flex"
              >
                Sign in
              </Button>
              <Button
                data-testid="nav-register"
                size="sm"
                onClick={() => navigate("/register")}
                className="rounded-full hidden sm:inline-flex"
              >
                Get started
              </Button>
            </>
          ) : null}

          <Button
            data-testid={TESTIDS.mobileMenuToggle}
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {open && (
        <nav className="md:hidden border-t border-border/60 bg-card">
          <div className="px-4 py-3 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId + "-mobile"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "px-3 py-2 rounded-lg text-base font-medium",
                    isActive ? "bg-primary/10 text-primary" : "text-foreground/90 hover:bg-muted"
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
            <NavLink to="/settings" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-base font-medium text-foreground/90 hover:bg-muted">
              Settings
            </NavLink>
            {!loading && user ? (
              <NavLink to="/profile" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-base font-medium text-primary">
                {user.username} · profile
              </NavLink>
            ) : !loading ? (
              <div className="flex gap-2 pt-2 border-t border-border/40 mt-2">
                <Button size="sm" variant="outline" className="flex-1 rounded-full" onClick={() => { setOpen(false); navigate("/login"); }}>
                  <LogIn className="h-4 w-4 mr-1.5" /> Sign in
                </Button>
                <Button size="sm" className="flex-1 rounded-full" onClick={() => { setOpen(false); navigate("/register"); }}>
                  Get started
                </Button>
              </div>
            ) : null}
          </div>
        </nav>
      )}
    </header>
  );
}

export function Wordmark({ className }) {
  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      <svg
        viewBox="0 0 36 24"
        className="h-7 w-auto"
        fill="none"
        aria-hidden="true"
      >
        <g>
          <circle cx="6" cy="7" r="3" fill="currentColor" opacity="0.85" />
          <circle cx="18" cy="7" r="3" fill="currentColor" opacity="0.55" />
          <circle cx="30" cy="7" r="3" fill="currentColor" opacity="0.35" />
          <circle cx="6" cy="17" r="3" fill="currentColor" opacity="0.4" />
          <circle cx="18" cy="17" r="3" fill="currentColor" opacity="0.7" />
          <circle cx="30" cy="17" r="3" fill="currentColor" opacity="0.95" />
        </g>
        <path d="M3 12 Q18 -2 33 12" stroke="currentColor" strokeWidth="1.2" strokeDasharray="2 2" opacity="0.45" />
      </svg>
      <span className="font-display text-xl font-semibold tracking-tight leading-none">
        Play Tsoro
      </span>
    </span>
  );
}
